// Connexion et étapes complémentaires : vérification MFA, changement de mot de passe imposé, enrôlement MFA.

import { useRef, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { verifierMotDePasse } from '@carburanttrack/shared';
import { api, ErreurApi } from '../../api/client';
import { useSession } from '../../app/session';
import { BoutonTheme } from '../../app/Coquille';
import { MessageErreur, Message } from '../../composants/Etats';
import { dateHeure } from '../../composants/format';
import '../../composants/presentation.css';

interface ComptePresentation {
  cle: string;
  libelle: string;
  role: string;
  roleLibelle: string;
  personne: string;
  fonction: string | null;
  organisation: string;
  courriel: string;
}

interface ComptesPresentation {
  comptes: ComptePresentation[];
  motDePasse: string;
  mention: string;
}

/** Comptes de présentation : la route n'existe qu'en environnement de présentation (404 ailleurs, aucun bloc affiché). */
function useComptesPresentation() {
  return useQuery({
    queryKey: ['comptes-presentation'],
    queryFn: async () => {
      try {
        return (await api<ComptesPresentation>('/api/v1/auth/presentation/comptes')).donnees;
      } catch (e) {
        if (e instanceof ErreurApi && e.statut === 404) return null;
        throw e;
      }
    },
    retry: false,
    staleTime: Infinity,
  });
}

function Cadre({ titre, children, apres }: { titre: string; children: React.ReactNode; apres?: React.ReactNode }) {
  return (
    <div className="page-connexion">
      <aside className="volet-marque" aria-label="CarburantTrack">
        <div className="volet-marque-tete">
          <span className="marque-connexion">
            <img src="/marque/pictogramme.png" alt="" className="seulement-jour" />
            <img src="/marque/pictogramme-clair.png" alt="" className="seulement-nuit" />
            CarburantTrack
          </span>
          <BoutonTheme persister={false} />
        </div>
        <div className="volet-marque-texte">
          <h1>Le suivi national des carburants, de la cuve à la décision.</h1>
          <p>Stocks, livraisons et alertes de toutes les installations du Gabon, partagés entre l’État, les opérateurs et les stations.</p>
        </div>
        <div className="volet-marque-pied">
          <img src="/marque/ministere-embleme.png" alt="" />
          <span>
            <b>Ministère du Pétrole et du Gaz</b>
            <span className="attenue">République gabonaise</span>
          </span>
        </div>
      </aside>
      <main className="volet-formulaire">
        <section className="carte-connexion" aria-labelledby="titre-connexion">
          <h2 id="titre-connexion">{titre}</h2>
          {children}
        </section>
        {apres}
      </main>
    </div>
  );
}

export function PageConnexion() {
  const { etat, recharger } = useSession();
  const etape = etat?.etape;
  if (etape === 'mfa') return <VerificationMfa onTermine={recharger} />;
  if (etape === 'mot_de_passe') return <ChangementMotDePasse onTermine={recharger} impose />;
  if (etape === 'enrolement_mfa') return <EnrolementMfa onTermine={recharger} />;
  return <FormulaireConnexion onTermine={recharger} />;
}

function FormulaireConnexion({ onTermine }: { onTermine: () => Promise<void> }) {
  const [courriel, setCourriel] = useState('');
  const [motDePasse, setMotDePasse] = useState('');
  const [erreur, setErreur] = useState<unknown>(null);
  const [envoi, setEnvoi] = useState(false);
  // Compte de présentation choisi par bouton ; remis à zéro dès que l'utilisateur modifie les champs.
  const [comptePresentation, setComptePresentation] = useState<string | null>(null);
  const boutonConnexion = useRef<HTMLButtonElement>(null);
  const naviguer = useNavigate();
  const presentation = useComptesPresentation();

  async function soumettre(e: FormEvent) {
    e.preventDefault();
    setEnvoi(true);
    setErreur(null);
    try {
      await api('/api/v1/auth/etat');
      const corps = comptePresentation === courriel ? { courriel, motDePasse, presentation: true } : { courriel, motDePasse };
      const r = await api<{ etape: string }>('/api/v1/auth/connexion', { corps });
      await onTermine();
      if (r.donnees.etape === 'complete') naviguer('/');
    } catch (err) {
      setErreur(err);
    } finally {
      setEnvoi(false);
    }
  }

  function choisirCompte(compte: ComptePresentation) {
    if (!presentation.data) return;
    setErreur(null);
    setCourriel(compte.courriel);
    setMotDePasse(presentation.data.motDePasse);
    setComptePresentation(compte.courriel);
    boutonConnexion.current?.focus();
  }

  const verrou = erreur instanceof ErreurApi && erreur.code === 'compte_verrouille' ? (erreur.details as { verrouilleJusquA?: string } | null)?.verrouilleJusquA : null;
  const blocPresentation =
    presentation.data && presentation.data.comptes.length > 0 ? (
      <section className="comptes-presentation" aria-labelledby="titre-comptes-presentation">
        <div className="comptes-presentation-tete">
          <h2 id="titre-comptes-presentation">Comptes de présentation</h2>
          <span className="sur-titre">Environnement de présentation uniquement</span>
        </div>
        <p>Comptes fictifs. Choisissez un profil : le courriel et le mot de passe se remplissent, puis appuyez sur « Se connecter ».</p>
        <div className="grille" role="group" aria-label="Choisir un compte de présentation">
          {presentation.data.comptes.map((compte) => (
            <button
              key={compte.cle}
              type="button"
              className="bouton"
              aria-pressed={comptePresentation === compte.courriel}
              onClick={() => choisirCompte(compte)}
              title={`${compte.personne}, ${compte.fonction ?? compte.roleLibelle}`}
            >
              <span>{compte.libelle}</span>
              <small>{compte.organisation}</small>
            </button>
          ))}
        </div>
      </section>
    ) : null;
  return (
    <Cadre titre="Connexion" apres={blocPresentation}>
      <form onSubmit={soumettre} noValidate>
        <div className="champ">
          <label htmlFor="courriel">Adresse de courriel</label>
          <input
            id="courriel"
            type="email"
            autoComplete="username"
            required
            value={courriel}
            onChange={(e) => {
              setCourriel(e.target.value);
              setComptePresentation(null);
            }}
          />
        </div>
        <div className="champ">
          <label htmlFor="mot-de-passe">Mot de passe</label>
          <input
            id="mot-de-passe"
            type="password"
            autoComplete="current-password"
            required
            value={motDePasse}
            onChange={(e) => {
              setMotDePasse(e.target.value);
              setComptePresentation(null);
            }}
          />
        </div>
        {erreur !== null && <MessageErreur erreur={erreur} titre="Connexion refusée" />}
        {verrou && <Message type="avertissement">Nouvel essai possible après {dateHeure(verrou)}.</Message>}
        <button ref={boutonConnexion} type="submit" className="bouton primaire" style={{ width: '100%', minHeight: 50 }} disabled={envoi || !courriel || !motDePasse}>
          {envoi ? 'Connexion en cours' : 'Se connecter'}
        </button>
      </form>
    </Cadre>
  );
}

function VerificationMfa({ onTermine }: { onTermine: () => Promise<void> }) {
  const [code, setCode] = useState('');
  const [erreur, setErreur] = useState<unknown>(null);
  const naviguer = useNavigate();
  async function soumettre(e: FormEvent) {
    e.preventDefault();
    setErreur(null);
    try {
      const r = await api<{ etape: string }>('/api/v1/auth/mfa/verifier', { corps: { code } });
      await onTermine();
      if (r.donnees.etape === 'complete') naviguer('/');
    } catch (err) {
      setErreur(err);
    }
  }
  return (
    <Cadre titre="Second facteur">
      <form onSubmit={soumettre}>
        <p>Saisissez le code à six chiffres de votre application d’authentification, ou un code de secours.</p>
        <div className="champ">
          <label htmlFor="code-mfa">Code</label>
          <input id="code-mfa" inputMode="numeric" autoComplete="one-time-code" value={code} onChange={(e) => setCode(e.target.value.trim())} />
        </div>
        {erreur !== null && <MessageErreur erreur={erreur} titre="Code refusé" />}
        <div className="actions">
          <button type="submit" className="bouton primaire" disabled={code.length < 6}>
            Valider
          </button>
          <AnnulerConnexion />
        </div>
      </form>
    </Cadre>
  );
}

function AnnulerConnexion() {
  const { deconnecter } = useSession();
  return (
    <button type="button" className="bouton" onClick={() => void deconnecter()}>
      Annuler
    </button>
  );
}

export function ChangementMotDePasse({ onTermine, impose }: { onTermine: () => Promise<void>; impose: boolean }) {
  const [actuel, setActuel] = useState('');
  const [nouveau, setNouveau] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [erreur, setErreur] = useState<unknown>(null);
  const [succes, setSucces] = useState(false);
  const { etat } = useSession();
  const naviguer = useNavigate();
  const politique = verifierMotDePasse(nouveau, { courriel: etat?.utilisateur?.courriel });
  const differents = confirmation.length > 0 && confirmation !== nouveau;

  async function soumettre(e: FormEvent) {
    e.preventDefault();
    setErreur(null);
    try {
      const r = await api<{ etape: string }>('/api/v1/auth/mot-de-passe', { corps: { motDePasseActuel: actuel, nouveauMotDePasse: nouveau } });
      setSucces(true);
      setActuel('');
      setNouveau('');
      setConfirmation('');
      await onTermine();
      if (impose && r.donnees.etape === 'complete') naviguer('/');
    } catch (err) {
      setErreur(err);
    }
  }

  const formulaire = (
    <form onSubmit={soumettre}>
      {impose && <p>Votre mot de passe initial doit être remplacé avant de continuer.</p>}
      <div className="champ">
        <label htmlFor="mdp-actuel">Mot de passe actuel</label>
        <input id="mdp-actuel" type="password" autoComplete="current-password" value={actuel} onChange={(e) => setActuel(e.target.value)} />
      </div>
      <div className="champ">
        <label htmlFor="mdp-nouveau">Nouveau mot de passe</label>
        <input id="mdp-nouveau" type="password" autoComplete="new-password" aria-describedby="mdp-regle" aria-invalid={nouveau.length > 0 && !politique.valide} value={nouveau} onChange={(e) => setNouveau(e.target.value)} />
        <span id="mdp-regle" className="aide">
          Au moins 12 caractères ; une phrase de passe est recommandée. Les mots de passe courants sont refusés.
        </span>
        {nouveau.length > 0 && politique.motifs.map((m) => <span key={m} className="erreur-champ">{m}</span>)}
      </div>
      <div className="champ">
        <label htmlFor="mdp-confirmation">Confirmation</label>
        <input id="mdp-confirmation" type="password" autoComplete="new-password" aria-invalid={differents} value={confirmation} onChange={(e) => setConfirmation(e.target.value)} />
        {differents && <span className="erreur-champ">Les deux saisies diffèrent.</span>}
      </div>
      {erreur !== null && <MessageErreur erreur={erreur} titre="Changement refusé" />}
      {succes && !impose && <Message type="succes">Mot de passe modifié ; vos autres sessions ont été fermées.</Message>}
      <div className="actions">
        <button type="submit" className="bouton primaire" disabled={!actuel || !politique.valide || nouveau !== confirmation}>
          Enregistrer le mot de passe
        </button>
        {impose && <AnnulerConnexion />}
      </div>
    </form>
  );
  return impose ? <Cadre titre="Nouveau mot de passe">{formulaire}</Cadre> : formulaire;
}

export function EnrolementMfa({ onTermine, integre = false }: { onTermine: () => Promise<void>; integre?: boolean }) {
  const [donnees, setDonnees] = useState<{ secret: string; uri: string; qrSvg: string } | null>(null);
  const [code, setCode] = useState('');
  const [codesSecours, setCodesSecours] = useState<string[] | null>(null);
  const [erreur, setErreur] = useState<unknown>(null);
  const naviguer = useNavigate();

  async function demarrer() {
    setErreur(null);
    try {
      setDonnees((await api<{ secret: string; uri: string; qrSvg: string }>('/api/v1/auth/mfa/enrolement', { corps: {} })).donnees);
    } catch (err) {
      setErreur(err);
    }
  }
  async function confirmer(e: FormEvent) {
    e.preventDefault();
    setErreur(null);
    try {
      const r = await api<{ codesSecours: string[] }>('/api/v1/auth/mfa/confirmation', { corps: { code } });
      setCodesSecours(r.donnees.codesSecours);
    } catch (err) {
      setErreur(err);
    }
  }

  const contenu = codesSecours ? (
    <div>
      <Message type="succes">Second facteur activé.</Message>
      <p>Conservez ces dix codes de secours hors ligne : chacun permet une seule connexion sans votre téléphone. Ils ne seront plus affichés.</p>
      <ol style={{ columns: 2, fontFamily: 'monospace', fontSize: 15 }}>
        {codesSecours.map((c) => (
          <li key={c}>{c}</li>
        ))}
      </ol>
      <button
        type="button"
        className="bouton primaire"
        onClick={() =>
          void onTermine().then(() => {
            if (!integre) naviguer('/');
          })
        }
      >
        J’ai conservé mes codes
      </button>
    </div>
  ) : !donnees ? (
    <div>
      <p>{integre ? 'Activez un second facteur pour renforcer la protection de votre compte.' : 'Votre rôle impose un second facteur d’authentification. Préparez une application d’authentification (TOTP) sur votre téléphone.'}</p>
      {erreur !== null && <MessageErreur erreur={erreur} />}
      <div className="actions">
        <button type="button" className="bouton primaire" onClick={() => void demarrer()}>
          Commencer l’enrôlement
        </button>
        {!integre && <AnnulerConnexion />}
      </div>
    </div>
  ) : (
    <form onSubmit={confirmer}>
      <p>Scannez ce code avec votre application, puis saisissez le code à six chiffres affiché.</p>
      <img src={`data:image/svg+xml;base64,${btoa(donnees.qrSvg)}`} alt="Code QR d’enrôlement du second facteur" width={200} height={200} style={{ display: 'block', margin: '0 auto', background: '#ffffff' }} />
      <p className="attenue" style={{ wordBreak: 'break-all' }}>
        Saisie manuelle de la clé : <code>{donnees.secret}</code>
      </p>
      <div className="champ">
        <label htmlFor="code-enrolement">Code à six chiffres</label>
        <input id="code-enrolement" inputMode="numeric" autoComplete="one-time-code" value={code} onChange={(e) => setCode(e.target.value.trim())} />
      </div>
      {erreur !== null && <MessageErreur erreur={erreur} titre="Code refusé" />}
      <button type="submit" className="bouton primaire" disabled={!/^\d{6}$/.test(code)}>
        Activer le second facteur
      </button>
    </form>
  );
  return integre ? contenu : <Cadre titre="Second facteur obligatoire">{contenu}</Cadre>;
}
